import type { ColumnDef } from "@tanstack/react-table";

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { AssetGroup, Hoid, HoidPartner } from "../data";
import type { DateRange } from "./dateRangePresets";
import type { SortOrder } from "./MobileSortRow";

import { Button as ButtonV2 } from "../../../Button";
import { FilterIcon, PlusIcon } from "../../../Icons";
import { SearchInputV2 } from "../../../SearchInputV2";
import { TableMobile } from "../../../Table";
import { TableState } from "../../../TableState";
import { Typo } from "../../../Typo";
import { useDebounceFn } from "../../../utils/debounce";
import { useReloadScope } from "../../periodLoading";
import { getAllTimeDateRange } from "../../providers/getDefaultDateRange";
import { useSharedRebateProgramsContext } from "../providers/SharedRebateProgramsProvider";
import { TableStateMessage } from "../TableStateMessage";
import { useNoRebateProgramsTranslations } from "../useNoRebateProgramsTranslations";
import { MobileKpiRail } from "./MobileKpiRail";
import { MobileProgramCard } from "./MobileProgramCard";
import { MobileProgramCardSkeleton } from "./MobileProgramCardSkeleton";
import { MobileProgramDetailSheet } from "./MobileProgramDetailSheet";
import { MobileProgramFilterSheet } from "./MobileProgramFilterSheet";
import { MobileSortRow } from "./MobileSortRow";

const SEARCH_DEBOUNCE_MS = 1000;
const EMPTY_DISMISSED_NOTIFICATION_IDS: ReadonlySet<string> = new Set();

// TableMobile requires a ColumnDef[] even in cardBody-only mode — search,
// sort, filter and pagination are all driven by the shared report context,
// not by TanStack's own row model, so a single dummy id column is enough
// (matches `PerformanceReport/mobile/MobileRowList.tsx`'s `ROW_COLUMNS`).
const PROGRAM_COLUMNS: ColumnDef<Hoid, unknown>[] = [
  { id: "id", accessorKey: "id" },
];

export type MobileSharedRebateProgramsViewProps = {
  /** Present when this sheet replaced the Sub IB drawer — see `SharedRebateProgramsView`'s own doc comment. */
  onProgramDrawerBack?: () => void;
  /** Opens the shared `ArchiveProgramModal` — owned by `SharedRebateProgramsView`, not this component. */
  onArchive: (hoid: Hoid) => void;
  /** Reuses `SharedRebateProgramsView`'s own `handleViewAnalytics` (deep-link + toast-fallback logic lives there once, not duplicated here). */
  onViewAnalytics: (hoid: Hoid) => void;
  getClientDataHref?: (hoid: Hoid, dateRange: DateRange) => string | undefined;
  onNavigateToClientsReport?: (href: string) => void;
  /** Live catalogue; null falls back to the mock in `catalogue.ts`. */
  assets?: AssetGroup[] | null;
  showSubIbStatus?: boolean;
  phase2Enabled?: boolean;
  dismissedRejectionNotificationIds?: ReadonlySet<string>;
  onDismissRejectedNotification?: (partner: HoidPartner) => Promise<void>;
};

/**
 * Mobile rendering path for the Shared Rebate Programs tab (PART-4052).
 * Starts at the tab's own content (search+filter row onward) — the page
 * title, tabs, and "New program" button live one level up in
 * `SubIBReportPage` and are unaffected by this ticket (see
 * PART-4052-plan.md's scope correction). Consumes the exact same
 * `useSharedRebateProgramsContext()` the desktop view uses — no new data
 * plumbing, only new rendering.
 */
export const MobileSharedRebateProgramsView = ({
  onProgramDrawerBack,
  onArchive,
  onViewAnalytics,
  getClientDataHref,
  onNavigateToClientsReport,
  assets,
  showSubIbStatus,
  phase2Enabled,
  dismissedRejectionNotificationIds = EMPTY_DISMISSED_NOTIFICATION_IDS,
  onDismissRejectedNotification,
}: MobileSharedRebateProgramsViewProps) => {
  const { t } = useTranslation();
  const reload = useReloadScope();
  const {
    state,
    actions,
    options,
    rows,
    totalCount,
    isError,
    retryTable,
    stats,
    isStatsError,
    isCsvLoading,
    exportCsv,
    openFilter,
    openProgramDetails,
    selectedProgramId,
    closeProgramDetails,
    filterOpen,
    closeFilter,
    allPrograms,
    fetchProgramDetailPatch,
    splitEmptyState,
    hasNoRebatePrograms,
    statusPending,
    requestCreateProgram,
  } = useSharedRebateProgramsContext();
  const noProgramsText = useNoRebateProgramsTranslations();
  const showNoProgramsPage = splitEmptyState && hasNoRebatePrograms;
  const visibleRows = useMemo(
    () =>
      rows.map((hoid) => ({
        ...hoid,
        partners: hoid.partners.map((partner) =>
          partner.rejectedNotificationId &&
          dismissedRejectionNotificationIds.has(partner.rejectedNotificationId)
            ? { ...partner, rejectedNotificationId: null }
            : partner,
        ),
      })),
    [rows, dismissedRejectionNotificationIds],
  );
  const visibleAllPrograms = useMemo(
    () =>
      allPrograms.map((hoid) => ({
        ...hoid,
        partners: hoid.partners.map((partner) =>
          partner.rejectedNotificationId &&
          dismissedRejectionNotificationIds.has(partner.rejectedNotificationId)
            ? { ...partner, rejectedNotificationId: null }
            : partner,
        ),
      })),
    [allPrograms, dismissedRejectionNotificationIds],
  );

  // Same resolution order as the desktop view: the currently displayed page
  // first, the full (mock/Storybook) roster as a fallback.
  const selectedProgram =
    visibleRows.find((h) => h.id === selectedProgramId) ??
    visibleAllPrograms.find((h) => h.id === selectedProgramId) ??
    null;

  const [searchInput, setSearchInput] = useState(state.searchQuery);
  const [commitSearch, cancelCommitSearch] = useDebounceFn(
    actions.setSearchQuery,
    SEARCH_DEBOUNCE_MS,
  );
  useEffect(() => cancelCommitSearch, [cancelCommitSearch]);
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    commitSearch(value);
  };

  const pageSize = state.pageSize;

  // Base 1 for the payout period — canvas's `activeCount` is
  // `1 + (subFilter ? 1 : 0) + (statusFilter ? 1 : 0)`: the filter sheet's
  // date range always has a real preset selected (defaults to All time,
  // PART-4130, never "none"), so it counts as active from first load.
  const activeFilterCount =
    1 +
    (state.selected.subIb?.length ? 1 : 0) +
    (state.selected.status?.length ? 1 : 0);

  const sortBy = state.sortBy ?? "lastPayment";
  const sortOrder: SortOrder = state.sortOrder ?? "desc";

  const cardBody = MobileProgramCard({
    onOpen: (hoid) => openProgramDetails(hoid.id),
    sortBy: phase2Enabled ? sortBy : undefined,
  });

  const committedDateRange =
    state.dateRange.from && state.dateRange.to
      ? { from: state.dateRange.from, to: state.dateRange.to }
      : getAllTimeDateRange();

  const noProgramsState = (
    <TableStateMessage
      state="empty"
      card
      compact
      title={noProgramsText.title}
      description={noProgramsText.description}
      action={
        <ButtonV2
          variant="brand"
          size="md"
          isLoading={statusPending}
          onClick={() => requestCreateProgram()}
          data-testid="shared_rebate_programs_empty_new_program_button"
        >
          <div className="flex items-center gap-2">
            <PlusIcon className="h-4 w-4" />
            {noProgramsText.action}
          </div>
        </ButtonV2>
      }
    />
  );
  const emptyState = (
    <TableState
      variant="empty"
      title={t("subIBReport_emptyTitle", "No data available")}
      description={t(
        "subIBReport_emptyDescription",
        "Try changing your filters or add a Sub IB",
      )}
    />
  );
  const errorState = (
    <TableState
      variant="error"
      title={t("subIBReport_errorTitle", "Unable to load data")}
      description={t("subIBReport_errorDescription", "Refresh to try again")}
      actionLabel={t("subIBReport_errorRetry", "Refresh")}
      onAction={retryTable}
    />
  );

  return (
    // grid grid-cols-1, not flex flex-col: MobileKpiRail's horizontal-scroll
    // strip below has a hardcoded minWidth: "640px" inner track. A flex-col
    // ancestor has no min-width:0 by default, so that 640px min-content
    // leaks upward and stretches this whole page wider than the viewport —
    // grid rows get min-width:0 automatically (see PageSection's own doc
    // comment, and SharedRebateProgramsView's desktop stats strip, which
    // uses this exact "mt-10 grid grid-cols-1" pattern for the same reason).
    <div className="grid grid-cols-1">
      {showNoProgramsPage ? (
        <div className="mt-2">{noProgramsState}</div>
      ) : (
        <>
          {/* Sticky search + filter row — top-[64px], below the shared mobile
          Header (~64px), per packages/ui/CLAUDE.md's stacked-sticky-toolbar
          convention (same offset PerformanceReport/mobile/index.tsx uses). */}
          <div className="sticky top-[64px] z-10 flex items-center gap-2 bg-surface-primary py-2">
            <div className="flex-1">
              <label htmlFor="mobile-rebate-search" className="sr-only">
                {t("subIBReport_searchLabel", "Search")}
              </label>
              <SearchInputV2
                id="mobile-rebate-search"
                name="mobile-rebate-search"
                value={searchInput}
                onChange={handleSearchChange}
                placeholder={t(
                  "subIBReport_searchPlaceholder",
                  "Search rebate program",
                )}
                clearLabel={t("subIBReport_clearSearch", "Clear search")}
              />
            </div>
            <button
              type="button"
              onClick={openFilter}
              aria-label={t("subIBReport_filters", "Filter")}
              // oxlint-disable-next-line partners-custom-rule/data-testid-format -- legacy id selected by packages/e2e; never rename existing ids
              data-testid="mobile-rebate-programs-filter-button"
              // 40×40 per canvas — matches SearchInputV2's own h-10, so this
              // row's tallest control is consistent at 40px (the "≈56px" sticky
              // offset below assumes py-2 (16) + a 40px control).
              className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-brand-default"
            >
              <FilterIcon
                height={20}
                width={20}
                // "icon-inverse-primary" isn't a real DS token (no `inverse` key
                // under `icon` in tailwind.config.ts) — was silently a no-op, so
                // the icon fell back to the inherited dark text color instead of
                // white. `text-inverse-primary` is the token that actually
                // resolves to white (`--text-inverse-primary`).
                className="text-text-inverse-primary"
              />
              {activeFilterCount > 0 && (
                <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-surface-inverse-primary px-1">
                  <Typo
                    element="span"
                    type="label2XS"
                    textColor="text-text-inverse-primary"
                  >
                    {activeFilterCount}
                  </Typo>
                </span>
              )}
            </button>
          </div>

          {/* mt-2 (8px) here + the search/filter row's own py-2 bottom padding
          (8px) nets the canvas's 16px search+filter→metrics gap. */}
          <div className="mt-2">
            <MobileKpiRail
              stats={stats}
              isStatsError={isStatsError}
              isFullReload={reload.full.stats}
              isPeriodReload={reload.period.stats}
            />
          </div>

          {/* Sticky sort + export row — top-[calc(64px+56px)]: the header (64px)
          plus this stuck-above row's own height (py-2 + control ≈ 56px),
          matching PerformanceReport/mobile/index.tsx's exact arithmetic.
          mt-2 (8px) + this row's own py-2 top padding (8px) nets the
          canvas's 16px metrics→sort/export gap. */}
          <div className="sticky top-[calc(64px+56px)] z-10 mt-2 bg-surface-primary py-2">
            <MobileSortRow
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={(by, order) => actions.setSort(by, order)}
              onExport={() => void exportCsv()}
              isExporting={isCsvLoading}
            />
          </div>

          {/* No margin here on purpose — the sort/export row's own py-2 bottom
          padding (8px) plus the first card's own py-4 top padding (16px,
          MobileProgramCardRow's `py-4`, untouched by cellContainerClassName
          since that only reaches TableMobile's own row wrapper) already nets
          the canvas's 24px sort/export→card-list gap on its own. An earlier
          version of this fix added an mt-4 (16px) on top of both paddings,
          netting 40px — the card's own inner padding was missed because it
          lives one level deeper than the row wrapper this file controls.
          reload.full.table swaps in MobileProgramCardSkeleton entirely,
          rather than passing isLoading to TableMobile — its own built-in
          skeleton (TableMobileSkeletonLoader) is a bordered, rounded card
          shaped like the Clients list, not this flush, divider-separated
          one, so it would visibly reflow the page once data arrives (same
          reasoning as PerformanceReport/mobile/index.tsx's identical swap
          for MobileRowListSkeleton). */}
          {reload.full.table ? (
            <MobileProgramCardSkeleton />
          ) : (
            <TableMobile
              data={visibleRows}
              columns={PROGRAM_COLUMNS}
              cardBody={cardBody}
              getRowId={(row) => row.id}
              showPagination
              enableServerSidePagination
              pageSize={pageSize}
              pageCount={Math.max(1, Math.ceil(totalCount / pageSize))}
              totalCount={totalCount}
              state={{ pagination: { pageIndex: state.page, pageSize } }}
              onPaginationChange={(updater) => {
                const next =
                  typeof updater === "function"
                    ? updater({ pageIndex: state.page, pageSize })
                    : updater;
                actions.setPage(next.pageIndex);
              }}
              isError={isError}
              errorComponent={errorState}
              emptyComponent={emptyState}
              cellContainerClassName="!my-0 !rounded-none !border-x-0 !border-t-0 !border-b !border-stroke-primary !p-0"
              onRetry={retryTable}
              filterFns={{} as never}
            />
          )}
        </>
      )}

      <MobileProgramFilterSheet
        open={filterOpen}
        onClose={closeFilter}
        subIbOptions={options.subIb}
        statusOptions={options.status}
        committedSubIb={state.selected.subIb ?? []}
        committedStatus={state.selected.status ?? []}
        committedDateRange={committedDateRange}
        onApply={({ subIb, status, dateRange }) => {
          actions.setSelected("subIb", subIb.length ? subIb : null);
          actions.setSelected("status", status.length ? status : null);
          actions.setDateRange(dateRange);
          closeFilter();
        }}
      />

      <MobileProgramDetailSheet
        hoid={selectedProgram}
        onClose={closeProgramDetails}
        onBack={
          onProgramDrawerBack
            ? () => {
                closeProgramDetails();
                onProgramDrawerBack();
              }
            : undefined
        }
        fetchDetail={fetchProgramDetailPatch}
        pageDateRange={state.dateRange}
        onArchive={() => {
          if (selectedProgram) onArchive(selectedProgram);
        }}
        onViewAnalytics={onViewAnalytics}
        getClientDataHref={getClientDataHref}
        onNavigateToClientsReport={onNavigateToClientsReport}
        assets={assets}
        showSubIbStatus={showSubIbStatus}
        onDismissRejectedNotification={(partner) => {
          void onDismissRejectedNotification?.(partner);
        }}
        phase2Enabled={phase2Enabled}
      />
    </div>
  );
};
